/**
 * TenantHeader Component
 * Light header bar for the tenant admin application.
 *
 * Ported from legacy Layout.tsx (frontend-tenant) -- adapted for verisa_v1.
 *
 * Features:
 * - Installation logo, its alt text and the link's accessible name from the
 *   BrandingProvider - no vendor literal in this file (SPEC-PLATFORM-BRANDING
 *   RULE-012, SCENARIO-005; the built-in path lives in branding/defaults.ts)
 * - The logo resolves for this caller, never to `/`: the agent chooser when
 *   they can use more than one agent, inside their only agent otherwise
 *   (TASK-TSURF-LOGO-TO-CHOOSER as corrected by D-TSG-16)
 * - Light background (#FAFAFA), dark text, 57px height
 * - Workspace name with switcher popover on the left
 * - Agent breadcrumb with switcher popover when inside agent detail
 * - User avatar + dropdown menu on the right (name, email, logout)
 * - Hamburger menu icon on mobile to toggle sidebar
 * - The staff badge for a caller the server resolves unrestricted
 *   (SPEC-TENANT-SURFACES RULE-012, TASK-TSURF-W7-STAFF-BADGES)
 */

import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/ManageAccounts';
import { Trans } from '@lingui/react/macro';
import { msg, select } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { useAuth } from '../../store/AuthContext';
import { useAgentNoun } from '../../utils/agentNoun';
import { useBranding } from '../../branding/BrandingProvider';
import { DefaultBranding } from '../../branding/defaults';
import { colors } from '../../theme/colors';
import ItemSwitcher from '../shared/ItemSwitcher';
import { AccessibilityDrawer } from '../accessibility';
import { FeatureGate } from '../../feature-gate';
import { LocalePicker } from '../shared/LocalePicker';
import { useQuery } from '@tanstack/react-query';
import StatusBadge from '../shared/StatusBadge';
import { useSurfaceSet } from '../../surfaces';
import { resolveLogoTarget } from '../../apps/tenant/landing';
import { useAgentStart, useLandingPredicate } from '../../apps/tenant/useLandingPredicate';
import { tenantAgentOptionsQueryOptions } from '../../api/agentPosture';
import AdvancedModeHint from './AdvancedModeHint';

/**
 * The registry key of the user's own settings page (D-TSG-02). A LITERAL, read
 * from config/contracts/spec-tenant-surfaces.registry.yaml - the SPA holds
 * no copy of the registry (SPEC-TENANT-SURFACES RULE-016).
 */
const MY_SETTINGS_SURFACE = 'tenant.my-settings';

/** Where that page is mounted (buildWorkspaceRoutes, `my-settings`). */
const MY_SETTINGS_PATH = '/my-settings';

/**
 * The logo block's appearance, UNCHANGED by TASK-TSURF-LOGO-TO-CHOOSER and
 * lifted out only so the linked and the unlinked arm cannot drift apart.
 */
const logoSx = {
  display: { xs: 'none', md: 'flex' },
  alignItems: 'center',
  textDecoration: 'none',
  transition: 'opacity 0.2s',
  '&:hover': { opacity: 0.8 },
} as const;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface AgentOption {
  id: string;
  name: string;
}

export interface WorkspaceOption {
  id: string;
  name: string;
}

export interface TenantHeaderProps {
  /** Callback to toggle the mobile sidebar drawer */
  onMobileToggle?: () => void;
  /** Agent name to show in breadcrumb when inside agent detail */
  agentName?: string;
  /** Current agent ID (to highlight in the switcher) */
  agentId?: string;
  /** List of all agents for the switcher dropdown */
  agents?: AgentOption[];
  /** Callback when selecting a different agent from the switcher */
  onAgentSwitch?: (agentId: string) => void;
  /** Current workspace name to display in the breadcrumb */
  workspaceName?: string;
  /** Current workspace ID (to highlight in the switcher) */
  workspaceId?: string;
  /** List of all workspaces for the switcher dropdown */
  workspaces?: WorkspaceOption[];
  /** Callback when selecting a different workspace from the switcher */
  onWorkspaceSwitch?: (id: string) => void;
  /** Callback for the "Create workspace" button in the switcher */
  onWorkspaceCreate?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * TenantHeader renders the top AppBar for the tenant admin layout.
 *
 * Responsive behavior:
 * - Mobile (< 600px): Shows hamburger icon to open the sidebar drawer
 * - Desktop: No hamburger; sidebar is always visible
 *
 * Uses useAuth() to display user info and provide logout.
 */
const TenantHeader: React.FC<TenantHeaderProps> = ({
  onMobileToggle,
  agentName,
  agentId,
  agents,
  onAgentSwitch,
  workspaceName,
  workspaceId,
  workspaces,
  onWorkspaceSwitch,
  onWorkspaceCreate,
}) => {
  const { _: lingui } = useLingui();
  const { noun, NounDef, nounDef, nounPluralDef } = useAgentNoun();
  const { user, logout } = useAuth();
  // The installation brand, read from the ONE owner of GET /api/v1/branding
  // (SPEC-FRONTEND-RULE-077, SPEC-PLATFORM-BRANDING-UI-004). The header never
  // fetches: `branding` already IS `DefaultBranding` while the read is in flight
  // and after it fails, so the built-in logo and name render silently in both
  // states and there is no broken image and no empty box (RULE-001, D-BR-15).
  const { branding } = useBranding();
  const surfaces = useSurfaceSet();
  // RULE-016 guarantees a non-empty name server-side and parseBranding refuses an
  // empty one, so this tail is belt-and-braces rather than a second policy.
  const productName = branding.product_name || DefaultBranding.product_name;
  // `logo_url` is nullable by CONTRACT-001 - null means "no asset uploaded", not
  // "the read failed" - and the built-in path is typed nullable with it, which is
  // the only reason for the second `??`. The literal itself lives in
  // defaults.ts and may not be repeated here (RULE-012).
  const logoUrl = branding.logo_url ?? DefaultBranding.logo_url ?? undefined;

  // Avatar menu anchor
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  // Workspace switcher anchor
  const [workspaceSwitcherAnchorEl, setWorkspaceSwitcherAnchorEl] = useState<null | HTMLElement>(null);

  // Agent switcher anchor
  const [agentSwitcherAnchorEl, setAgentSwitcherAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const displayWorkspaceName = workspaceName || lingui(msg`Workspace`);

  // The user's own settings home (D-TSG-02). Gated on the SAME resolved surface
  // set the route builder filters with (SPEC-TENANT-SURFACES RULE-006 (2)), so
  // the menu entry and the route appear and disappear together and the entry can
  // never point at an unregistered path. The row is configurable: false, so in
  // practice it is usable for everyone - including on the me/surfaces error
  // path, where the server pins exactly the non-configurable keys (D-TSG-09).
  const mySettingsUsable = surfaces.isUsable(MY_SETTINGS_SURFACE);
  // The switcher's "Create workspace" entry is the registered section
  // tenant.header.workspace-create (TASK-TSURF-MESSAGE-ACTIONS-AND-SECTIONS): with
  // the surface unusable the switcher gets no onCreateNew and renders no entry.
  // Its route, POST /api/v1/workspaces, is refused server-side by the same row.
  const workspaceCreateUsable = surfaces.isUsable("tenant.header.workspace-create");

  // ---------------------------------------------------------------------
  // Where the logo goes (TASK-TSURF-LOGO-TO-CHOOSER, owner ruling 2026-09-23)
  // ---------------------------------------------------------------------
  //
  // It used to be `component="a" href="/"`, and `/` is the workspace index,
  // which is <LandingRedirect scope="workspace" group="index" /> - the landing
  // chain. For a caller whose landing is an agent (D-375) the shell's only home
  // control therefore returned them INTO that agent, and the management UI had
  // no reachable entry. The owner ruled the logo goes to the agent chooser;
  // login-time landing is unchanged.
  //
  // The target is RESOLVED, never a literal `/agents`
  // (SPEC-TENANT-SURFACES-RULE-005(b)): a control that targets a registrable
  // surface asks the SAME resolved answer the route builders filter the routes
  // with, so it can never name a route they did not register. The predicate is
  // `useLandingPredicate` - the ONE hook every <LandingRedirect> in routes.tsx
  // resolves through (the release gate, this caller's permission AND the
  // resolved surface set), imported rather than re-assembled, so the logo and
  // the router cannot answer differently for the same caller.
  //
  // The chooser row is `configurable: false` (RULE-005(a)), so in practice it is
  // usable for everyone, including on the me/surfaces error path where the
  // server pins exactly the non-configurable keys. It is still ASKED, and the
  // fallback is still written, because the predicate is the contract and not the
  // registry as it stands today. `CHOOSER_CANDIDATE` is also the FIRST
  // workspace-index candidate, so the two legs below agree by construction.
  // When nothing is usable the answer is `null` and the logo renders with no
  // link at all, exactly as a group with no usable child renders no redirect.
  //
  // CORRECTED 2026-09-23 (D-TSG-16, owner ruling "Választó, ha több ügynök"):
  // the chooser ONLY when the caller can use more than one agent; with exactly
  // one, the logo goes inside it (landing.ts `resolveLogoTarget`). The agent list
  // is the landing redirect's own read - the ONE key + fetcher on it
  // (SPEC-FRONTEND-RULE-077) - so the header adds no second fetcher.
  // AMENDED 2026-09-24 (D-SCA-10, owner ruling): a caller who can manage the
  // tenant's members gets the chooser whatever the agent count - inside an agent
  // this header offers only "My settings" and logout, and the members page lives
  // in the workspace layout. Decided in `resolveLogoTarget` by the same predicate
  // passed below, so the header itself does not change.
  const landingAgents = useQuery(tenantAgentOptionsQueryOptions());
  const logoTarget = resolveLogoTarget({
    agents: landingAgents.data ?? null,
    usable: useLandingPredicate(),
    // D-TSG-32: inside the only agent, the logo opens its start page.
    agentStart: useAgentStart(),
  });
  // The workspace-name breadcrumb leads out of an agent to the SAME resolved target
  // as the logo (TASK-TSURF-HEADER-INAPP-LINKS), never a literal '/agents'
  // (RULE-005(b)). Outside an agent, or with nothing usable, it is plain text.
  const breadcrumbTarget = agentName ? logoTarget : null;
  const breadcrumbSx = {
    maxWidth: { xs: 100, md: 200 },
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: 500,
    fontSize: '14px',
    textDecoration: 'none',
  } as const;

  // The staff badge (SPEC-TENANT-SURFACES RULE-012(a), TASK-TSURF-W7-STAFF-BADGES),
  // ONLY when the server's answer says `unrestricted` (ui.view_all: every surface,
  // the tenant's hidden ones included); under act-as the answer is the target's.
  // Product name from the BrandingProvider, never a vendor literal (SPEC-PLATFORM-
  // BRANDING RULE-012); its own msgid, because "{productName} staff" is the admin
  // roles panel's PLURAL heading ("munkatársak") and this names one person. Beside
  // the account button from `sm` up (no room below), and beside the user's name
  // in the account menu at every width.
  const staffBadge = surfaces.unrestricted ? (
    <StatusBadge status="info" label={lingui(msg`${productName} staff member`)} />
  ) : null;

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: '100%',
        zIndex: (theme) => theme.zIndex.drawer + 3,
        bgcolor: '#FAFAFA',
        borderBottom: '1px solid',
        borderColor: 'divider',
        color: 'text.primary',
      }}
    >
      <Toolbar
        sx={{
          height: '57px',
          minHeight: '57px !important',
          maxHeight: '57px',
          px: 2.5,
          py: 0,
          alignItems: 'center',
        }}
      >
        {/* Mobile hamburger button (workspace pages only) */}
        {!agentName && (
          <IconButton
            color="inherit"
            aria-label={lingui(msg`Open navigation menu`)}
            edge="start"
            onClick={onMobileToggle}
            sx={{
              mr: 1,
              display: { sm: 'none' },
              p: 0.5,
              borderRadius: 1,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <MenuIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}

        {/* Left section -- Logo + workspace name + agent breadcrumb */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Logo - a ROUTER link to the resolved chooser, not an anchor to `/`.
              `href` full-page-reloads the SPA; `component={RouterLink}` keeps
              the click in-app. The two arms share one `sx`, so the appearance,
              the hover and the responsive display are identical whether or not
              the caller has somewhere to go. */}
          {logoTarget === null ? (
            <Box sx={logoSx}>
              <img
                src={logoUrl}
                alt={productName}
                style={{ height: 24, width: 24 }}
              />
            </Box>
          ) : (
            <Box
              component={RouterLink}
              to={logoTarget}
              aria-label={lingui(msg`${productName} homepage`)}
              sx={logoSx}
            >
              <img
                src={logoUrl}
                alt={productName}
                style={{ height: 24, width: 24 }}
              />
            </Box>
          )}

          {/* Separator */}
          <Box
            sx={{
              display: { xs: 'none', md: 'inline-flex' },
              width: 24,
              height: 24,
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: 500,
              color: 'text.disabled',
            }}
          >
            /
          </Box>

          {/* Workspace name — inside an agent, a ROUTER link out to the resolved
              chooser (the logo's target); otherwise plain text. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {breadcrumbTarget === null ? (
              <Typography
                component="span"
                sx={{ ...breadcrumbSx, color: colors.text.default, cursor: 'default' }}
              >
                {displayWorkspaceName}
              </Typography>
            ) : (
              <Typography
                component={RouterLink}
                to={breadcrumbTarget}
                data-testid="tenant-header-workspace-crumb"
                sx={{
                  ...breadcrumbSx,
                  color: colors.text.clickable || 'primary.main',
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.8 },
                }}
              >
                {displayWorkspaceName}
              </Typography>
            )}

            {/* Workspace switcher chevron */}
            <IconButton
              onClick={(e) => setWorkspaceSwitcherAnchorEl(e.currentTarget)}
              disabled={!workspaces || workspaces.length === 0}
              sx={{
                ml: 0.5,
                p: 0.5,
                width: 20,
                height: 20,
                borderRadius: '6px',
                color: '#9CA3AF',
                opacity: (!workspaces || workspaces.length === 0) ? 0.5 : 1,
                '&:hover': { color: colors.text.default },
              }}
              aria-label={lingui(msg`Switch workspace`)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m7 15 5 5 5-5" />
                <path d="m7 9 5-5 5 5" />
              </svg>
            </IconButton>
          </Box>

          {/* Agent breadcrumb segment with switcher — shown when inside agent detail */}
          {agentName && (
            <>
              <Box
                sx={{
                  display: 'inline-flex',
                  width: 24,
                  height: 24,
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'text.disabled',
                }}
              >
                /
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  component="span"
                  sx={{
                    maxWidth: { xs: 120, md: 250 },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontWeight: 500,
                    fontSize: '14px',
                    color: colors.text.default,
                  }}
                >
                  {agentName}
                </Typography>

                {/* Agent switcher chevron */}
                {agents && agents.length > 1 && (
                  <IconButton
                    onClick={(e) => setAgentSwitcherAnchorEl(e.currentTarget)}
                    sx={{
                      ml: 0.5,
                      p: 0.5,
                      width: 20,
                      height: 20,
                      borderRadius: '6px',
                      color: '#9CA3AF',
                      '&:hover': { color: colors.text.default },
                    }}
                    aria-label={lingui(msg`${select(NounDef, { other: `Switch ${nounDef}` })}`)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m7 15 5 5 5-5" />
                      <path d="m7 9 5-5 5 5" />
                    </svg>
                  </IconButton>
                )}
              </Box>
            </>
          )}
        </Box>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Language Picker */}
        <Box sx={{ mr: 2 }}>
          <LocalePicker compact />
        </Box>

        <FeatureGate feature="global.accessibility">
          <AccessibilityDrawer />
        </FeatureGate>

        {/* The ONE advanced-mode hint (D-TSG-05), beside the account menu whose
            "Saját beállítások" entry it names. It renders nothing unless the
            caller holds ui.use_advanced in a tenant with the tier on AND has
            never decided anything about the switch. */}
        <AdvancedModeHint />

        {staffBadge && (
          <Box
            data-testid="staff-badge"
            sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', ml: 1 }}
          >
            {staffBadge}
          </Box>
        )}

        {/* User avatar + dropdown menu */}
        <div>
          <IconButton
            size="large"
            aria-label={lingui(msg`Account menu`)}
            aria-controls="tenant-header-menu"
            aria-haspopup="true"
            onClick={handleMenuOpen}
            color="inherit"
          >
            <Avatar sx={{ width: 28, height: 28 }}>
              {user?.name?.charAt(0)?.toUpperCase() ?? '?'}
            </Avatar>
          </IconButton>

          <Menu
            id="tenant-header-menu"
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={menuOpen}
            onClose={handleMenuClose}
            slotProps={{
              paper: {
                sx: { minWidth: 200, mt: 0.5 },
              },
            }}
          >
            {/* User info (non-interactive) */}
            <Box sx={{ px: 2, py: 1 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: colors.text.primary }}
              >
                {user?.name ?? lingui(msg`User`)}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: colors.text.secondary }}
              >
                {user?.email ?? ''}
              </Typography>
              {staffBadge && (
                <Box data-testid="staff-badge-menu" sx={{ display: 'flex', mt: 0.5 }}>
                  {staffBadge}
                </Box>
              )}
            </Box>
            <Divider />

            {/* The user's own settings (D-TSG-02) - above Logout, which was the
                menu's only item before this change. A ROUTER link, like the logo
                and the breadcrumb: a plain `href` reloaded the whole SPA
                (TASK-TSURF-HEADER-INAPP-LINKS). */}
            {mySettingsUsable && (
              <MenuItem
                component={RouterLink}
                to={MY_SETTINGS_PATH}
                data-surface={MY_SETTINGS_SURFACE}
                onClick={handleMenuClose}
              >
                <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                  <SettingsIcon fontSize="small" />
                </Box>
                <Trans>My settings</Trans>
              </MenuItem>
            )}

            {/* Logout */}
            <MenuItem onClick={handleLogout}>
              <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                <LogoutIcon fontSize="small" />
              </Box>
              <Trans>Logout</Trans>
            </MenuItem>
          </Menu>
        </div>
      </Toolbar>

      {/* Workspace Switcher Popover */}
      <ItemSwitcher
        anchorEl={workspaceSwitcherAnchorEl}
        open={Boolean(workspaceSwitcherAnchorEl)}
        onClose={() => setWorkspaceSwitcherAnchorEl(null)}
        items={workspaces ?? []}
        currentItemId={workspaceId}
        currentItemName={displayWorkspaceName}
        onSelect={(item) => {
          setWorkspaceSwitcherAnchorEl(null);
          onWorkspaceSwitch?.(item.id);
        }}
        onCreateNew={workspaceCreateUsable ? onWorkspaceCreate : undefined}
        searchPlaceholder={lingui(msg`Search workspace...`)}
        currentLabel={lingui(msg`Current Workspace`)}
        switchLabel={lingui(msg`Switch to`)}
        createLabel={lingui(msg`Create workspace`)}
        uniqueKey={`ws-${workspaces?.length ?? 0}`}
      />

      {/* Agent Switcher Popover */}
      {agents && agents.length > 0 && (
        <ItemSwitcher
          anchorEl={agentSwitcherAnchorEl}
          open={Boolean(agentSwitcherAnchorEl)}
          onClose={() => setAgentSwitcherAnchorEl(null)}
          items={agents}
          currentItemId={agentId}
          currentItemName={agentName}
          onSelect={(item) => {
            setAgentSwitcherAnchorEl(null);
            if (item.id !== agentId) onAgentSwitch?.(item.id);
          }}
          searchPlaceholder={lingui(msg`Search ${nounPluralDef}...`)}
          currentLabel={lingui(msg`Current ${noun}`)}
          switchLabel={lingui(msg`Switch to`)}
          uniqueKey={`agent-${agents.length}`}
        />
      )}
    </AppBar>
  );
};

export default TenantHeader;
